import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { getCurrentUser } from "@/lib/auth";
import Card, { CardHeader, CardContent } from "@/components/shared/ui/Card";

interface EOIWidgetProps {
  projectId: string;
}

const interestLevels = [
  { value: "due_diligence_undecided", label: "Due Diligence - Undecided" },
  { value: "at_closed_safe", label: "At Closed Family & Friends Safe Round" },
  { value: "at_seed_preseed", label: "At Seed or Pre-Seed" },
  { value: "will_fully_finance", label: "Will Fully Finance" },
  { value: "not_for_me", label: "Not For Me" },
  { value: "speak_future", label: "Speak in the Future" },
  { value: "at_series_a", label: "At Series A" },
  { value: "at_series_bc", label: "At Series B or C..." },
  { value: "advisory_only", label: "Advisory Only" },
  { value: "morale_support", label: "Morale Support" },
];

export default function EOIWidget({ projectId }: EOIWidgetProps) {
  const [status, setStatus] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchEOI();
  }, [projectId]);

  const fetchEOI = async () => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      if (!currentUser) return;

      const { data, error: fetchError } = await supabase
        .from("expression_of_interest")
        .select("status, notes, updated_at")
        .eq("project_id", projectId)
        .eq("user_id", currentUser.id)
        .maybeSingle();

      if (fetchError) {
        throw fetchError;
      }

      if (!data) {
        // No record exists yet, create one
        await createEOI(currentUser.id);
      } else {
        setStatus(data.status || "due_diligence_undecided");
        setNotes(data.notes || "");
        setLastUpdated(data.updated_at);
      }
    } catch (err) {
      console.error("Error fetching EOI:", err);
    } finally {
      setLoading(false);
    }
  };

  const createEOI = async (userId: string) => {
    const { data, error } = await supabase
      .from("expression_of_interest")
      .insert({
        project_id: projectId,
        user_id: userId,
        status: "due_diligence_undecided",
        notes: "",
      })
      .select()
      .single();

    if (!error && data) {
      setStatus(data.status);
      setNotes("");
    }
  };

  const updateEOI = async (newStatus: string, newNotes?: string) => {
    try {
      setSaving(true);
      const currentUser = await getCurrentUser();
      if (!currentUser) return;

      const { error } = await supabase
        .from("expression_of_interest")
        .update({
          status: newStatus,
          notes: newNotes !== undefined ? newNotes : notes,
          updated_at: new Date().toISOString(),
        })
        .eq("project_id", projectId)
        .eq("user_id", currentUser.id);

      if (error) throw error;
      setLastUpdated(new Date().toISOString());
    } catch (err) {
      console.error("Error updating EOI:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    setStatus(newStatus);
    updateEOI(newStatus);
  };

  const handleNotesBlur = () => {
    updateEOI(status, notes);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Card variant="bordered">
        <CardHeader color="purple">
          <h3 className="text-base sm:text-lg font-bold text-black">
            Expression of Interest
          </h3>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-gray-500 text-xs sm:text-sm">
            Loading...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="bordered">
      <CardHeader color="purple">
        <h3 className="text-base sm:text-lg font-bold text-black">
          Expression of Interest
        </h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Status Dropdown */}
          <div>
            <label className="block text-xs sm:text-sm font-bold text-black mb-2">
              Current Interest Level
            </label>
            <select
              value={status}
              onChange={handleStatusChange}
              disabled={saving}
              className="w-full px-3 py-2 border-2 border-black rounded bg-white text-black text-xs sm:text-sm font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              {interestLevels.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>

          {/* Notes Textarea */}
          <div>
            <label className="block text-xs sm:text-sm font-bold text-black mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={handleNotesBlur}
              disabled={saving}
              placeholder="Add any questions or notes..."
              rows={3}
              className="w-full px-3 py-2 border-2 border-black rounded bg-white text-black text-xs sm:text-sm resize-none hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: "'Courier New', monospace" }}
            />
          </div>

          {/* Last Updated */}
          {lastUpdated && (
            <div className="pt-3 border-t-2 border-black">
              <div className="text-xs text-gray-500">Last Updated</div>
              <div className="text-xs sm:text-sm font-bold text-black mt-1">
                {formatDate(lastUpdated)}
              </div>
            </div>
          )}

          {/* Saving Indicator */}
          {saving && (
            <div className="text-xs text-gray-500 italic">Saving...</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
